import React, { useState, useEffect, useRef, useCallback, MutableRefObject, RefObject } from 'react';
import { Row, Upload, Modal, Col } from 'antd';
import styled from 'styled-components';
import './styles.css';

import { EditorState, convertToRaw, ContentState } from 'draft-js';
import { Editor } from 'react-draft-wysiwyg';
import draftToHtml from 'draftjs-to-html';
import htmlToDraft from 'html-to-draftjs';

import { useKeyBinding } from '../../hooks';
import { UploadOutlined, PlusOutlined } from '@ant-design/icons';
import config from '../../utils/config';
import { ButtonType, TAButton, TATextArea } from '../widgets';

const CommentContainer = styled.div`
	padding: 12px 16px;
	border-radius: 4px;
	border: 1px solid rgb(223, 225, 230);
	color: rgb(137, 147, 164);
	cursor: pointer;
	user-select: none;

	&:hover {
		border: 1px solid rgb(193, 199, 208);
	}
`;
function dataURLtoFile(dataurl, filename) {
	var arr = dataurl.split(','),
		mime = arr[0].match(/:(.*?);/)[1],
		bstr = atob(arr[1]),
		n = bstr.length,
		u8arr = new Uint8Array(n);

	while (n--) {
		u8arr[n] = bstr.charCodeAt(n);
	}
	return new File([u8arr], filename, { type: mime });
}

export const CommentInput = React.forwardRef<any, any>(
	(
		{
			onFocus,
			onChange,
			saveText = 'Save',
			cancelText = 'Cancel',
			onBlur,
			disabled,
			richEditor = false,
			value,
			onSave,
			onCancel,
			loading,
			focused = false
		},
		ref
	) => {
		let [commentState, setCommentState] = useState<any>({
			focused: focused,
			content: richEditor ? EditorState.createEmpty() : ''
		});
		let [commentHasText, setCommentHasText] = useState(false);
		let [currentAttachmentFile, setCurrentAttachmentFile] = useState([]);

		let commentAreaRef = useRef((ref as MutableRefObject<any>)?.current || null);

		const onContentChange = e => {
			setCommentHasText(e.getCurrentContent().hasText());
			//Have to do this check because editor's state change is triggered on focuse and blur so we need to ignore changes if actual content is the same
			// if (richEditor && e.getCurrentContent() === commentState.content.getCurrentContent()) return;
			let val = richEditor ? e : e.target.value;
			setCommentState({ ...commentState, content: val });
			onChange && onChange(val);
		};

		const setFocus = () => {
			if (commentState.focused) return;
			setCommentState({ ...commentState, focused: true });
			if (commentAreaRef.current) {
				richEditor ? commentAreaRef.current.focusEditor() : commentAreaRef.current.focus();
				onFocus && onFocus();
			}
		};

		const onInputBlur = () => {
			setCommentState({ ...commentState, focused: false });
			onBlur && onBlur();
		};

		useKeyBinding(
			{
				keys: 'm',
				callback: setFocus,
				ref: window.document
			},
			[commentState.focused]
		);

		useEffect(() => {
			if (focused !== commentState.focused) setCommentState({ ...commentState, focused: focused });
		}, [focused]);

		useEffect(() => {
			if (!value) return;
			let state;
			if (richEditor) {
				let content = htmlToDraft(value);
				const contentState = ContentState.createFromBlockArray(content.contentBlocks);
				state = EditorState.createWithContent(contentState);
			} else state = value;
			setCommentState({ ...commentState, content: state });
		}, [value]);
		const getBase64 = file =>
			new Promise((resolve, reject) => {
				const reader = new FileReader();
				reader.readAsDataURL(file);

				reader.onload = () => resolve(reader.result);

				reader.onerror = error => reject(error);
			});

		const [previewVisible, setPreviewVisible] = useState(false);
		const [previewImage, setPreviewImage] = useState('');
		const [previewTitle, setPreviewTitle] = useState('');

		const [fileList, setFileList] = useState([]);

		const handleCancel = () => setPreviewVisible(false);

		const handlePreview = async file => {
			if (!file.url && !file.preview) {
				file.preview = await getBase64(file.originFileObj);
			}

			setPreviewImage(file.url || file.preview);
			setPreviewVisible(true);
			setPreviewTitle(file.name || file.url.substring(file.url.lastIndexOf('/') + 1));
		};

		const handleChange = ({ fileList: newFileList }) => setFileList(newFileList);

		const onCommentSave = () => {
			if (onSave) {
				onSave(
					richEditor
						? draftToHtml(convertToRaw(commentState.content.getCurrentContent()))
						: commentState.content,
					String(fileList?.map(({ response }) => response)),
					err => {
						if (!err) {
							setCommentState({
								focused: false,
								content: richEditor ? EditorState.createEmpty() : ''
							});
							setFileList([]);
						}
					}
				);
			}
			setCommentState({
				focused: false,
				content: richEditor ? EditorState.createEmpty() : ''
			});
			setCommentHasText(false);
			setFileList([]);
		};

		const uploadButton = (
			<div>
				<PlusOutlined />
				<div
					style={{
						marginTop: 8
					}}
				>
					Upload
				</div>
			</div>
		);
		return (
			<div
				onKeyDown={e => {
					e.stopPropagation();
				}}
			>
				{!commentState.focused && (
					<>
						<CommentContainer onClick={setFocus}>Add a comment ...</CommentContainer>
						<div className="text-tip">
							<strong>Pro tip:</strong>press<span className="keyboard-key-text">M</span>to comment
						</div>
					</>
				)}

				{richEditor ? (
					<Editor
						pastePlain
						stripPastedStyles
						toolbar={{
							options: ['inline', 'blockType', 'fontSize', 'list', 'colorPicker', 'link', 'emoji'],
							inline: {
								inDropdown: true,
								className: undefined,
								dropdownClassName: 'comment-editor--inline'
								// options: ['bold', 'italic']
							},
							link: {
								popupClassName: 'comment-editor--link',
								showOpenOptionOnHover: false
							},
							list: {
								inDropdown: true,
								options: ['unordered', 'ordered'],
								dropdownClassName: 'comment-editor--list'
							},
							colorPicker: {
								popupClassName: 'comment-editor--colorPicker',
								colors: [
									'rgb(255 255 255 / 0%)',
									'rgb(23, 43, 77)',
									'rgb(151, 160, 175)',
									'rgb(101, 84, 192)',
									'rgb(0, 184, 217)',
									'rgb(255, 86, 48)'
								],
								highlight: undefined
							}
						}}
						editorState={commentState.content}
						editorClassName={`comment-editor ${commentState.focused && 'comment-editor--focused'}`}
						onEditorStateChange={onContentChange}
						placeholder="Add a comment..."
						disabled={disabled || loading}
						ref={commentAreaRef}
						wrapperStyle={{ display: richEditor && commentState.focused ? 'initial' : 'none' }}
					/>
				) : (
					<TATextArea
						ref={commentAreaRef}
						placeholder="Add a comment..."
						value={commentState.content}
						onChange={onContentChange}
						disabled={disabled || loading}
						// className="comment-input"
						style={{ display: richEditor && commentState.focused ? 'none' : 'initial' }}
					/>
				)}
				<Row className="mt-2">
					<Col xs={24}>
						{/* <Upload
							action={config.api_url + config.URIs.upload_attachments}
							listType="picture-card"
							fileList={fileList}
							onPreview={handlePreview}
							onChange={handleChange}
						>
							{fileList.length >= 8 ? null : uploadButton}
						</Upload> */}
						<Modal visible={previewVisible} title={previewTitle} footer={null} onCancel={handleCancel}>
							<img
								alt="example"
								style={{
									width: '100%'
								}}
								src={previewImage}
							/>
						</Modal>
					</Col>
					{(commentHasText || fileList?.length !== 0) && (
						<Col xs={24}>
							<TAButton btntype={ButtonType.PRIMARY} className="w-100" onClick={onCommentSave}>
								{saveText}
							</TAButton>
						</Col>
					)}
				</Row>
			</div>
		);
	}
);
